import { useState, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useDepartment } from '../../context/DepartmentContext'
import { useToast } from '../../context/ToastContext'
import { fuzzyMatch, getProducts } from '../../data/products'
import { parsePosLine } from '../../lib/pdfPosExtract'
import { lookupProductMapping, saveProductMapping } from '../../lib/productMappings'
import { saveSalesBatch, saveFlagsBatch, deleteSalesForDate, verifySalesForDate } from '../../lib/store'
import { generateTransactionFlags } from '../../lib/flagging'
import { Upload, FileCheck, AlertCircle, Check, CalendarDays, Trash2, Info } from 'lucide-react'
import AddProductModal from './AddProductModal'
import DateInput from '../ui/DateInput'
import ArithmeticInput from '../ui/ArithmeticInput'

/*
 * POS PDF format (Makhado Park / Desres Kitchen):
 *
 *   Header:  Group   Qty   Sales R   %Sales   Cost R   Profit R
 *   Row:     CODE NAME   16   208.00   4.5%   0.00   208.00
 *   Subtotal:            1617.00   35.2%   300.06   1316.94
 *
 * We need column 2 (Qty, integer) and column 3 (Sales R, decimal).
 * NOT Cost R or Profit R which are columns 5 and 6.
 *
 * Sections: Kitchen, Bakery, Sandwich, Soft Drink, Tea, etc.
 * Each section has a header line and a subtotal line.
 */

// PDF sales parser uses the canonical parsePosLine from
// src/lib/pdfPosExtract.js — see imports at top of file. The local
// copy has been removed so future fixes flow from a single source.

// Parse all transactions from the PDF text. Section filtering has
// been removed — items from any POS section (Kitchen, Bakery, Soft
// Drink, Not Grouped, etc.) are returned. The matching pipeline
// downstream uses fuzzyMatch + saved product mappings to decide
// which items belong to the current department, so a kitchen item
// miscategorised under "Soft Drink" in the POS still gets imported.
function parseSalesSection(text /* department arg unused */) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  const rows = []
  let dateStr = null

  const fullMatch = text.match(/For The Period\s+(\d+)\/(\d+)\/(\d{4})/i)
  if (fullMatch) {
    const month = fullMatch[1].padStart(2, '0')
    const dayPart = fullMatch[2].padStart(2, '0')
    dateStr = `${fullMatch[3]}-${month}-${dayPart}`
  }
  if (!dateStr) {
    const dateMatch = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/)
    if (dateMatch) {
      dateStr = `${dateMatch[3]}-${dateMatch[2].padStart(2, '0')}-${dateMatch[1].padStart(2, '0')}`
    }
  }

  for (const line of lines) {
    const parsed = parsePosLine(line)
    if (parsed) {
      rows.push({ rawName: parsed.name, productCode: parsed.productCode, qty: parsed.qty, sales: parsed.sales })
    }
  }

  return { date: dateStr, rows }
}

async function extractPdfText(file, onProgress) {
  onProgress?.('Loading PDF library...')
  const { configurePdfWorker, pdfjsLib } = await import('../../lib/pdfWorker')
  configurePdfWorker()

  onProgress?.('Reading file...')
  const arrayBuffer = await file.arrayBuffer()

  onProgress?.('Parsing PDF structure...')
  const loadPromise = pdfjsLib.getDocument(arrayBuffer).promise
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('PDF loading timed out after 60 seconds. Try a smaller file.')), 60000)
  )
  const pdf = await Promise.race([loadPromise, timeoutPromise])

  let fullText = ''
  for (let i = 1; i <= pdf.numPages; i++) {
    onProgress?.(`Extracting text from page ${i} of ${pdf.numPages}...`)
    try {
      const page = await pdf.getPage(i)
      const content = await page.getTextContent()
      // ─── Diagnostic logging ─────────────────────────────────────────
      // Surface what pdf.js is actually extracting from the source PDF
      // before any of our reconstruction logic touches it. The most
      // likely iOS failure mode is items.length === 0 (glyph-encoded
      // text). Other modes show up as collapsed Y coordinates or
      // empty/garbled item.str values.
      console.log(`[PDF DEBUG] Page ${i}/${pdf.numPages} — items: ${content.items.length}`)
      if (content.items.length > 0) {
        console.log('[PDF DEBUG] First 10 item.str values:',
          content.items.slice(0, 10).map(it => JSON.stringify(it.str)))
        console.log('[PDF DEBUG] First 10 Y coords:',
          content.items.slice(0, 10).map(it => it.transform?.[5]))
      } else {
        console.warn(`[PDF DEBUG] Page ${i} has ZERO text items — likely glyph-encoded`)
      }
      // Reconstruct tabular layout using position data
      // Group items by Y position (same row), sort by X position (columns)
      const items = content.items.filter(item => item.str.trim())
      if (items.length === 0) continue
      // Group by Y coordinate (with tolerance for slight misalignment)
      const rows = []
      let currentRow = []
      let lastY = null
      const sorted = [...items].sort((a, b) => {
        // Sort top-to-bottom (higher Y = higher on page in PDF coords)
        const yDiff = b.transform[5] - a.transform[5]
        if (Math.abs(yDiff) > 3) return yDiff
        return a.transform[4] - b.transform[4] // left to right
      })
      for (const item of sorted) {
        const y = Math.round(item.transform[5])
        if (lastY !== null && Math.abs(y - lastY) > 3) {
          if (currentRow.length > 0) rows.push(currentRow)
          currentRow = []
        }
        currentRow.push(item)
        lastY = y
      }
      if (currentRow.length > 0) rows.push(currentRow)
      // Build text with multi-space column separators
      for (const row of rows) {
        row.sort((a, b) => a.transform[4] - b.transform[4])
        let lineText = ''
        let lastX = 0
        for (const item of row) {
          const x = item.transform[4]
          const gap = x - lastX
          // If gap > 20 units, treat as column separator
          if (lineText && gap > 20) {
            lineText += '   ' // triple space as column separator
          } else if (lineText && gap > 5) {
            lineText += ' '
          }
          lineText += item.str
          lastX = x + (item.width || item.str.length * 5)
        }
        fullText += lineText + '\n'
      }
    } catch (err) {
      console.warn(`Failed to parse PDF page ${i}:`, err)
      Sentry.captureException(err, { extra: { fn: 'PdfUpload.extractPdfText.page', pageIndex: i } })
      fullText += `\n[Page ${i} failed]\n`
    }
  }
  // ─── Diagnostic logging ───────────────────────────────────────────
  // Final synthesised text the parser will consume. If this is empty
  // or short despite items.length > 0 above, the position-grouping
  // logic collapsed everything into nothing usable.
  console.log('[PDF DEBUG] Final fullText length:', fullText.length)
  console.log('[PDF DEBUG] First 500 chars:', fullText.substring(0, 500))
  console.log('[PDF DEBUG] Line count:', fullText.split('\n').length)
  return fullText
}

export default function PdfUpload({ dataType, onSaved }) {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const fileRef = useRef()
  const [parsedFiles, setParsedFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [parseProgress, setParseProgress] = useState('')
  const [parseError, setParseError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [addProductTarget, setAddProductTarget] = useState(null)
  const [dateRange, setDateRange] = useState({ from: '', to: '' })

  const department = currentDepartmentId

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setLoading(true)
    setSaved(false)
    setParseError(null)

    try {
      const results = []

      for (let fi = 0; fi < files.length; fi++) {
        const file = files[fi]
        setParseProgress(`Processing file ${fi + 1} of ${files.length}: ${file.name}...`)

        try {
          const fullText = await extractPdfText(file, (msg) => {
            setParseProgress(`${file.name}: ${msg}`)
          })
          const result = parseSalesSection(fullText, department)

          // Match priority: saved manual mapping → exact productCode → fuzzyMatch
          const allProducts = getProducts(department)
          const matched = result.rows.map(row => {
            // 1. Saved mapping
            const mappedCode = lookupProductMapping(department, row.rawName)
            if (mappedCode) {
              const product = allProducts.find(p => p.code?.toLowerCase() === mappedCode.toLowerCase())
              if (product) {
                return {
                  ...row, matched: product, confidence: 100,
                  status: 'matched', selectedCode: product.code,
                }
              }
            }

            // 2. Exact productCode match
            if (row.productCode) {
              const product = allProducts.find(p => p.code?.toLowerCase() === row.productCode.toLowerCase())
              if (product) {
                return {
                  ...row, matched: product, confidence: 100,
                  status: 'matched', selectedCode: product.code,
                }
              }
            }

            // 3. Fuzzy fallback
            const match = fuzzyMatch(row.rawName, department)
            return {
              ...row,
              matched: match?.product || null,
              confidence: match?.confidence || 0,
              status: match
                ? match.confidence >= 85 ? 'matched' : 'fuzzy'
                : 'unknown',
              selectedCode: match?.product?.code || '',
            }
          })

          results.push({
            sourceFile: file.name,
            date: result.date || new Date().toISOString().split('T')[0],
            rows: matched,
          })
        } catch (err) {
          addToast(`Failed to parse ${file.name}: ${err.message}`, 'error')
          Sentry.captureException(err, { extra: { fn: 'PdfUpload.parseFile', fileName: file.name, department } })
          // Continue with remaining files
        }
      }

      if (results.length > 0) {
        setParsedFiles(prev => [...prev, ...results])
      } else {
        setParseError('No files could be parsed. Check the file format and try again.')
      }
    } catch (err) {
      setParseError('Failed to parse PDFs: ' + err.message)
      addToast('Failed to parse PDF: ' + err.message, 'error')
      Sentry.captureException(err, { extra: { fn: 'PdfUpload.handleFiles', department } })
    } finally {
      setLoading(false)
      setParseProgress('')
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const updateFileDate = (fileIdx, newDate) => {
    setParsedFiles(prev => {
      const updated = [...prev]
      updated[fileIdx] = { ...updated[fileIdx], date: newDate }
      return updated
    })
  }

  const removeFile = (fileIdx) => {
    setParsedFiles(prev => prev.filter((_, i) => i !== fileIdx))
  }

  const updateRowField = (fileIdx, rowIdx, field, value) => {
    setParsedFiles(prev => {
      const updated = [...prev]
      const rows = [...updated[fileIdx].rows]
      rows[rowIdx] = { ...rows[rowIdx], [field]: value }
      updated[fileIdx] = { ...updated[fileIdx], rows }
      return updated
    })
  }

  const removeRow = (fileIdx, rowIdx) => {
    setParsedFiles(prev => {
      const updated = [...prev]
      updated[fileIdx] = { ...updated[fileIdx], rows: updated[fileIdx].rows.filter((_, i) => i !== rowIdx) }
      return updated
    })
  }

  const updateMatch = (fileIdx, rowIdx, code) => {
    if (code === '__NEW__') {
      setAddProductTarget({ fileIdx, rowIdx })
      setShowAddProduct(true)
      return
    }
    setParsedFiles(prev => {
      const updated = [...prev]
      const rows = [...updated[fileIdx].rows]
      const product = getProducts(department).find(p => p.code === code)
      // Persist the manual mapping for future uploads.
      if (rows[rowIdx]?.rawName) {
        saveProductMapping(department, rows[rowIdx].rawName, code || null)
      }
      rows[rowIdx] = {
        ...rows[rowIdx],
        matched: product || null,
        selectedCode: code,
        status: product ? 'matched' : 'unknown',
        confidence: product ? 100 : 0,
      }
      updated[fileIdx] = { ...updated[fileIdx], rows }
      return updated
    })
  }

  const handleNewProductAdded = (product) => {
    if (addProductTarget) {
      const { fileIdx, rowIdx } = addProductTarget
      setParsedFiles(prev => {
        const updated = [...prev]
        const rows = [...updated[fileIdx].rows]
        rows[rowIdx] = {
          ...rows[rowIdx],
          matched: product,
          selectedCode: product.code,
          status: 'matched',
          confidence: 100,
        }
        updated[fileIdx] = { ...updated[fileIdx], rows }
        return updated
      })
    }
  }

  const applyDateRange = () => {
    if (!dateRange.from) return
    const from = new Date(dateRange.from)
    const to = dateRange.to ? new Date(dateRange.to) : from
    const days = Math.round((to - from) / 86400000) + 1

    if (days <= 0) { addToast('Invalid date range', 'warning'); return }

    setParsedFiles(prev => prev.map((pf, i) => {
      const d = new Date(from)
      d.setDate(d.getDate() + i)
      return { ...pf, date: d.toISOString().split('T')[0] }
    }))
    addToast(`Applied dates: ${dateRange.from} to ${to.toISOString().split('T')[0]}`, 'info')
  }

  const saveAll = async () => {
    let totalRows = 0
    let totalFlags = 0
    let totalDupes = 0
    const datesProcessed = new Set()

    try {
    for (const pf of parsedFiles) {
      const transactions = []

      for (const row of pf.rows) {
        if (!row.matched) continue
        transactions.push({
          date: pf.date,
          productCode: row.matched.code,
          productName: row.matched.name,
          category: row.matched.category,
          qtySold: row.qty,
          salesRands: row.sales,
          unitPrice: row.matched.unitPrice,
          qps: row.matched.qps,
          saleType: row.matched.saleType,
          totalSalesExpected: row.matched.unitPrice ? row.matched.unitPrice * row.qty : null,
          difference: row.matched.unitPrice ? row.sales - (row.matched.unitPrice * row.qty) : null,
          entryMode: 'pdf',
          sourceFile: pf.sourceFile,
        })
      }

      if (transactions.length === 0) continue

      // Delete existing sales for this date before saving to prevent accumulation
      if (!datesProcessed.has(pf.date)) {
        await deleteSalesForDate(department, pf.date)
      }
      datesProcessed.add(pf.date)

      const results = saveSalesBatch(department, transactions)
      totalRows += results.length
      totalDupes += results.filter(r => r.duplicate).length

      const allFlags = []
      for (const { record } of results) {
        const flags = generateTransactionFlags(record)
        allFlags.push(...flags)
      }
      if (allFlags.length > 0) {
        saveFlagsBatch(department, allFlags)
        totalFlags += allFlags.length
      }
    }

    const dayCount = datesProcessed.size
    addToast(
      `Saved ${totalRows} rows across ${dayCount} day${dayCount !== 1 ? 's' : ''}, ${totalFlags} flags raised${totalDupes > 0 ? `, ${totalDupes} duplicates merged` : ''}`,
      'success'
    )

    // Post-save verification — check Firestore matches what we just saved
    for (const d of datesProcessed) {
      verifySalesForDate(department, d).then(result => {
        if (!result.ok) {
          console.warn(`[VERIFY] ${d}:`, result.warnings)
          addToast(`Warning: data integrity issue for ${d} — check console`, 'warning')
        }
      }).catch(err => {
        console.warn('[VERIFY] verifySalesForDate failed:', err?.message)
        Sentry.captureException(err, { extra: { fn: 'PdfUpload.verifySalesForDate', department, date: d } })
      })
    }

    setSaved(true)
    onSaved?.()
    } catch (err) {
      console.error('[PdfUpload] saveAll failed:', err)
      addToast('Save failed — check console', 'error')
      Sentry.captureException(err, { extra: { fn: 'PdfUpload.saveAll', department, fileCount: parsedFiles.length } })
    }
  }

  const rowColor = (status) => {
    switch (status) {
      case 'matched': return 'bg-green-950/30'
      case 'fuzzy': return 'bg-amber-950/30'
      case 'unknown': return 'bg-red-950/30'
      default: return ''
    }
  }

  const totalDays = new Set(parsedFiles.map(f => f.date)).size
  const totalItems = parsedFiles.reduce((sum, f) => sum + f.rows.length, 0)
  const unknownCount = parsedFiles.reduce((sum, f) => sum + f.rows.filter(r => r.status === 'unknown').length, 0)
  const products = getProducts(department)

  return (
    <div className="space-y-4">
      {parsedFiles.length === 0 && !loading && (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center cursor-pointer hover:border-gray-500 transition-colors"
        >
          <Upload size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Drop PDF(s) here or click to upload</p>
          <p className="text-gray-600 text-xs mt-1">POS daily sales reports — select multiple files for multi-day batch import</p>
          <input ref={fileRef} type="file" accept=".pdf,application/pdf,application/x-pdf" multiple onChange={handleFiles} style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} />
        </div>
      )}

      {loading && (
        <div className="text-center py-8 space-y-2">
          <div className="w-6 h-6 border-2 border-brand-400 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">{parseProgress || 'Parsing PDF(s)...'}</p>
        </div>
      )}

      {parseError && !loading && parsedFiles.length === 0 && (
        <div className="bg-red-950/30 border border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-300">{parseError}</p>
            <button
              onClick={() => { setParseError(null); fileRef.current?.click() }}
              className="text-xs bg-red-800 hover:bg-red-700 text-white px-3 py-1.5 rounded mt-2"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {parsedFiles.length > 0 && !saved && (
        <div className="space-y-4">
          <div className="bg-brand-950/30 border border-brand-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CalendarDays size={20} className="text-brand-400" />
              <div>
                <p className="text-sm text-white font-medium">
                  {parsedFiles.length} file{parsedFiles.length !== 1 ? 's' : ''} — {totalDays} day{totalDays !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-500">
                  {totalItems} items total
                  {unknownCount > 0 && <span className="text-amber-400 ml-2">({unknownCount} unmatched)</span>}
                </p>
              </div>
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded"
            >
              + Add More PDFs
            </button>
            <input ref={fileRef} type="file" accept=".pdf,application/pdf,application/x-pdf" multiple onChange={handleFiles} style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} />
          </div>

          {totalDays <= 1 && (
            <div className="bg-amber-950/30 border border-amber-800 rounded-lg p-3 flex items-center gap-3">
              <Info size={16} className="text-amber-400 shrink-0" />
              <p className="text-xs text-amber-300">
                Only {totalDays} day detected. For single-day entries, consider using <strong>Single Day Entry</strong> mode for a faster workflow. You can still proceed with this upload if preferred.
              </p>
            </div>
          )}

          {/* Date range selector for multi-day uploads */}
          {parsedFiles.length > 1 && (
            <div className="flex items-center gap-3 bg-gray-800/50 rounded-lg px-4 py-3 flex-wrap">
              <span className="text-xs text-gray-400">Assign date range:</span>
              <DateInput value={dateRange.from} onChange={val => setDateRange(prev => ({ ...prev, from: val }))} />
              <span className="text-gray-600">to</span>
              <DateInput value={dateRange.to} onChange={val => setDateRange(prev => ({ ...prev, to: val }))} />
              <button
                onClick={applyDateRange}
                className="bg-brand-700 hover:bg-brand-600 text-white text-xs px-3 py-1 rounded"
              >
                Apply
              </button>
            </div>
          )}

          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-800" /> Matched</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-800" /> Fuzzy</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-800" /> Unknown</span>
          </div>

          {parsedFiles.map((pf, fi) => (
            <div key={fi} className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <FileCheck size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-white">{pf.sourceFile}</span>
                <span className="text-xs text-gray-500">{pf.rows.length} items</span>

                <div className="flex items-center gap-2 ml-auto">
                  <label className="text-xs text-gray-500">Date:</label>
                  <DateInput value={pf.date} onChange={val => updateFileDate(fi, val)} />
                  <button
                    onClick={() => removeFile(fi)}
                    className="text-gray-600 hover:text-red-400 ml-1"
                    title="Remove file"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 text-xs border-b border-gray-800">
                      <th className="text-left py-2 px-2">Raw Name</th>
                      <th className="text-left py-2 px-2">Match</th>
                      <th className="text-right py-2 px-2">Qty</th>
                      <th className="text-right py-2 px-2">Sales (R)</th>
                      <th className="text-center py-2 px-2">Conf</th>
                      <th className="py-2 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pf.rows.map((row, ri) => (
                      <tr key={ri} className={`border-b border-gray-800/50 ${rowColor(row.status)}`}>
                        <td className="py-2 px-2 text-gray-300 text-xs">{row.rawName}</td>
                        <td className="py-2 px-2">
                          {row.status === 'unknown' ? (
                            <select
                              value={row.selectedCode}
                              onChange={e => updateMatch(fi, ri, e.target.value)}
                              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
                            >
                              <option value="">Map to product...</option>
                              {products.map(p => (
                                <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
                              ))}
                              <option value="__NEW__">+ Create New Product</option>
                            </select>
                          ) : (
                            <span className="text-xs">{row.matched?.code} — {row.matched?.name}</span>
                          )}
                        </td>
                        <td className="py-1 px-2">
                          <ArithmeticInput
                            min="0"
                            value={row.qty}
                            onChange={(val) => updateRowField(fi, ri, 'qty', val == null ? 0 : Number(val))}
                            className="bg-transparent border border-transparent hover:border-gray-600 focus:border-brand-500 rounded px-1 py-0.5 text-xs text-white w-16 text-right outline-none"
                          />
                        </td>
                        <td className="py-1 px-2">
                          <ArithmeticInput
                            min="0"
                            value={row.sales}
                            onChange={(val) => updateRowField(fi, ri, 'sales', val == null ? 0 : Number(val))}
                            className="bg-transparent border border-transparent hover:border-gray-600 focus:border-brand-500 rounded px-1 py-0.5 text-xs text-white w-20 text-right outline-none"
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className={`text-xs ${row.confidence >= 85 ? 'text-green-400' : row.confidence >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                            {row.confidence}%
                          </span>
                        </td>
                        <td className="py-2 px-1">
                          <button onClick={() => removeRow(fi, ri)} className="text-gray-600 hover:text-red-400">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <button
            onClick={saveAll}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
          >
            <Check size={16} /> Confirm & Save All ({parsedFiles.length} file{parsedFiles.length !== 1 ? 's' : ''})
          </button>
        </div>
      )}

      {saved && (
        <div className="text-center py-8">
          <Check size={32} className="text-green-400 mx-auto mb-2" />
          <p className="text-green-400 text-sm">Data saved successfully</p>
          <button
            onClick={() => { setParsedFiles([]); setSaved(false) }}
            className="text-gray-400 text-xs mt-3 hover:text-white"
          >
            Upload more files
          </button>
        </div>
      )}

      {showAddProduct && (
        <AddProductModal
          department={department}
          prefillName={addProductTarget ? parsedFiles[addProductTarget.fileIdx]?.rows[addProductTarget.rowIdx]?.rawName : ''}
          onClose={() => { setShowAddProduct(false); setAddProductTarget(null) }}
          onProductAdded={handleNewProductAdded}
        />
      )}
    </div>
  )
}
